import React, { useState, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Grid,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Tooltip,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Search,
  Computer,
  Person,
  Description,
  LocalShipping,
  Clear,
  LocationOn,
  Business,
  Schedule,
  Print,
  Phone,
  Receipt,
} from '@mui/icons-material';
import { formatDateForDisplay } from '../utils/dateUtils';
import {
  equipmentAPI,
  contractsAPI,
  EquipmentDetails,
  EquipmentSearchResult,
  CounterReading,
} from '../services/api';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ContractInfo = Record<string, any>;

const Equipment: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [autocompleteOptions, setAutocompleteOptions] = useState<EquipmentSearchResult[]>([]);
  const [autocompleteLoading, setAutocompleteLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const [equipment, setEquipment] = useState<EquipmentDetails | null>(null);
  const [contracts, setContracts] = useState<ContractInfo[]>([]);
  const [counterReadings, setCounterReadings] = useState<CounterReading[]>([]);

  const [loadingContracts, setLoadingContracts] = useState(false);
  const [loadingCounters, setLoadingCounters] = useState(false);

  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [savingProration, setSavingProration] = useState(false);

  const handleAutocomplete = useCallback(async (query: string) => {
    if (query.length < 2) {
      setAutocompleteOptions([]);
      return;
    }
    setAutocompleteLoading(true);
    try {
      const results = await equipmentAPI.searchSerial(query);
      setAutocompleteOptions(results);
    } catch {
      setAutocompleteOptions([]);
    } finally {
      setAutocompleteLoading(false);
    }
  }, []);

  const handleSearch = useCallback(async (serial?: string) => {
    const term = serial || searchTerm.trim();
    if (!term) {
      setError('Por favor ingrese un número de serie');
      return;
    }

    setSearching(true);
    setError(null);
    setNotFound(false);
    setEquipment(null);
    setContracts([]);
    setCounterReadings([]);
    setSuccess(null);

    try {
      const equipmentData = await equipmentAPI.getBySerial(term);
      setEquipment(equipmentData);

      // Load related data in parallel
      const loadContracts = async () => {
        setLoadingContracts(true);
        try {
          const contractIds = [
            equipmentData.active_contract_id,
            equipmentData.leasing_contract_id,
            equipmentData.service_contract_id,
          ].filter((id): id is string => !!id);
          const uniqueIds = Array.from(new Set(contractIds));

          if (uniqueIds.length > 0) {
            const results = await Promise.all(
              uniqueIds.map(async (id) => {
                try {
                  return await contractsAPI.getDetails(id);
                } catch {
                  return null;
                }
              })
            );
            setContracts(results.filter(Boolean) as ContractInfo[]);
          }
        } catch (err) {
          console.error('Error loading contracts:', err);
        } finally {
          setLoadingContracts(false);
        }
      };

      const loadCounters = async () => {
        setLoadingCounters(true);
        try {
          const readings = await equipmentAPI.getCounterReadings(equipmentData.id);
          setCounterReadings(readings);
        } catch (err) {
          console.error('Error loading counter readings:', err);
        } finally {
          setLoadingCounters(false);
        }
      };

      await Promise.all([loadContracts(), loadCounters()]);
    } catch (err: any) {
      if (err.response?.status === 404) {
        setNotFound(true);
      } else {
        const detail = err.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : 'Error al buscar el equipo');
      }
    } finally {
      setSearching(false);
    }
  }, [searchTerm]);

  const handleClear = () => {
    setSearchTerm('');
    setEquipment(null);
    setContracts([]);
    setCounterReadings([]);
    setError(null);
    setNotFound(false);
    setSuccess(null);
    setAutocompleteOptions([]);
  };

  const handleToggleSkipProration = async () => {
    if (!equipment) return;
    setSavingProration(true);
    setError(null);
    try {
      const newValue = !equipment.skip_proration;
      await equipmentAPI.update(equipment.id, { skip_proration: newValue });
      setEquipment({ ...equipment, skip_proration: newValue });
      setSuccess(
        newValue
          ? 'Prorrateo desactivado para este equipo'
          : 'Prorrateo activado para este equipo'
      );
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Error al actualizar');
    } finally {
      setSavingProration(false);
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status) {
      case 'active': return 'success';
      case 'maintenance': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'active': return 'Activo';
      case 'maintenance': return 'Mantenimiento';
      case 'inactive': return 'Inactivo';
      case 'installed': return 'Instalado';
      case 'completed': return 'Completado';
      case 'cancelled': return 'Cancelado';
      default: return status;
    }
  };

  const getContractTypeLabel = (type: string) => {
    switch (type) {
      case 'leasing': return 'Leasing';
      case 'service': return 'Servicio';
      case 'combined': return 'Combinado';
      default: return type;
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return formatDateForDisplay(dateString);
  };

  const formatCurrency = (value?: string | number) => {
    if (value === undefined || value === null) return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return `$${num.toFixed(4)}`;
  };

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Equipos MFP
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Busque equipos por número de serie para ver información, contratos y lecturas
        </Typography>
      </Box>

      {/* Search Section */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Autocomplete
            freeSolo
            fullWidth
            options={autocompleteOptions}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.serial_number
            }
            renderOption={(props, option) => (
              <li {...props} key={typeof option === 'string' ? option : option.id}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {typeof option === 'string' ? option : option.serial_number}
                  </Typography>
                  {typeof option !== 'string' && (
                    <Typography variant="caption" color="text.secondary">
                      {option.manufacturer} {option.model_name} - {option.client_name || 'Sin cliente'}
                    </Typography>
                  )}
                </Box>
              </li>
            )}
            loading={autocompleteLoading}
            inputValue={searchTerm}
            onInputChange={(_, value) => {
              setSearchTerm(value.toUpperCase());
              handleAutocomplete(value);
            }}
            onChange={(_, value) => {
              if (value && typeof value !== 'string') {
                setSearchTerm(value.serial_number);
                handleSearch(value.serial_number);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Ingrese número de serie del MFP..."
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleSearch();
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Print color="action" />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <>
                      {autocompleteLoading ? <CircularProgress size={20} /> : null}
                      {searchTerm && (
                        <IconButton size="small" onClick={handleClear}>
                          <Clear />
                        </IconButton>
                      )}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Button
            variant="contained"
            size="large"
            onClick={() => handleSearch()}
            disabled={searching || !searchTerm.trim()}
            startIcon={searching ? <CircularProgress size={20} color="inherit" /> : <Search />}
            sx={{ minWidth: 150, py: 1.5 }}
          >
            {searching ? 'Buscando...' : 'Buscar'}
          </Button>
        </Box>
      </Paper>

      {/* Alerts */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}
      {notFound && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="subtitle1" fontWeight="bold">Equipo no encontrado</Typography>
          <Typography variant="body2">
            No se encontró ningún equipo con el número de serie "{searchTerm}".
          </Typography>
        </Alert>
      )}

      {/* Results */}
      {equipment && (
        <Box>
          <Grid container spacing={3}>
            {/* Equipment Info Card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Computer color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Información del Equipo</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Número de Serie</Typography>
                      <Typography variant="body1" fontWeight="bold">{equipment.serial_number}</Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Estado</Typography>
                      <Box>
                        <Chip
                          label={getStatusLabel(equipment.status)}
                          color={getStatusColor(equipment.status)}
                          size="small"
                        />
                      </Box>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Modelo</Typography>
                      <Typography variant="body1">
                        {equipment.manufacturer} {equipment.model_name}
                      </Typography>
                    </Grid>
                    {equipment.installation_date && (
                      <Grid size={6}>
                        <Typography variant="caption" color="text.secondary">Fecha Instalación</Typography>
                        <Typography variant="body2">
                          <Schedule fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                          {formatDate(equipment.installation_date)}
                        </Typography>
                      </Grid>
                    )}
                    {equipment.ekm_device_id && (
                      <Grid size={6}>
                        <Typography variant="caption" color="text.secondary">EKM Device ID</Typography>
                        <Typography variant="body2">{equipment.ekm_device_id}</Typography>
                      </Grid>
                    )}
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Último Contador B/N</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {equipment.last_meter_bw?.toLocaleString() || 0}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="caption" color="text.secondary">Último Contador Color</Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {equipment.last_meter_color?.toLocaleString() || 0}
                      </Typography>
                    </Grid>

                    {/* Skip Proration Toggle */}
                    <Grid size={12}>
                      <Divider sx={{ my: 1 }} />
                      <Tooltip
                        title="Si se activa, este equipo NO será prorrateado aunque tenga lecturas antiguas (>45 días). Se usarán los valores reales del contador."
                        arrow
                      >
                        <FormControlLabel
                          control={
                            <Switch
                              checked={equipment.skip_proration}
                              onChange={handleToggleSkipProration}
                              disabled={savingProration}
                              color="warning"
                            />
                          }
                          label={
                            <Box>
                              <Typography variant="body2" fontWeight="medium">
                                Omitir Prorrateo
                                {savingProration && (
                                  <CircularProgress size={14} sx={{ ml: 1, verticalAlign: 'middle' }} />
                                )}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Usar valores reales aunque las lecturas sean antiguas
                              </Typography>
                            </Box>
                          }
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Contact & Location Card */}
            <Grid size={{ xs: 12, md: 6 }}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Person color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Contacto y Ubicación</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  <Grid container spacing={2}>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Cliente</Typography>
                      <Typography variant="body1" fontWeight="bold">
                        <Business fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {equipment.client_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">ID Cliente</Typography>
                      <Typography variant="body2">{equipment.client_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Contacto</Typography>
                      <Typography variant="body1">
                        <Person fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {equipment.contact_name || 'No asignado'}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                      <Typography variant="body1">
                        <Phone fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {equipment.contact_phone || 'No asignado'}
                      </Typography>
                    </Grid>
                    <Grid size={12}>
                      <Typography variant="caption" color="text.secondary">Ubicación</Typography>
                      <Typography variant="body1">
                        <LocationOn fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                        {equipment.location || 'No especificada'}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Contracts Section */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Description color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Contratos Asociados</Typography>
                    {loadingContracts && <CircularProgress size={20} sx={{ ml: 2 }} />}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {contracts.length === 0 && !loadingContracts ? (
                    <Typography color="text.secondary">No hay contratos asociados</Typography>
                  ) : (
                    <Grid container spacing={2}>
                      {contracts.map((contract) => (
                        <Grid size={{ xs: 12, md: 6 }} key={contract.id}>
                          <Paper variant="outlined" sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                {contract.contract_number}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Chip label={getContractTypeLabel(contract.contract_type)} size="small" color="primary" variant="outlined" />
                                <Chip label={getStatusLabel(contract.status)} size="small" color={contract.status === 'active' ? 'success' : 'default'} />
                              </Box>
                            </Box>
                            <Grid container spacing={1}>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">Costo B/N</Typography>
                                <Typography variant="body2">{formatCurrency(contract.bw_cost_per_copy)}</Typography>
                              </Grid>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">Costo Color</Typography>
                                <Typography variant="body2">{formatCurrency(contract.color_cost_per_copy)}</Typography>
                              </Grid>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">Inicio</Typography>
                                <Typography variant="body2">{formatDate(contract.start_date)}</Typography>
                              </Grid>
                              <Grid size={6}>
                                <Typography variant="caption" color="text.secondary">Fin</Typography>
                                <Typography variant="body2">{formatDate(contract.end_date)}</Typography>
                              </Grid>
                            </Grid>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Counter Readings History */}
            <Grid size={12}>
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Receipt color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6">Historial de Contadores</Typography>
                    {loadingCounters && <CircularProgress size={20} sx={{ ml: 2 }} />}
                  </Box>
                  <Divider sx={{ mb: 2 }} />

                  {counterReadings.length === 0 && !loadingCounters ? (
                    <Typography color="text.secondary">No hay lecturas registradas</Typography>
                  ) : (
                    <TableContainer sx={{ maxHeight: 400 }}>
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            <TableCell>Fecha</TableCell>
                            <TableCell align="right">B/N</TableCell>
                            <TableCell align="right">Color</TableCell>
                            <TableCell>Método</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {counterReadings.map((reading) => (
                            <TableRow key={reading.id}>
                              <TableCell>{formatDate(reading.reading_date)}</TableCell>
                              <TableCell align="right">{reading.bw_counter?.toLocaleString()}</TableCell>
                              <TableCell align="right">{reading.color_counter?.toLocaleString()}</TableCell>
                              <TableCell>
                                <Chip label={reading.reading_method} size="small" variant="outlined" />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Empty State */}
      {!equipment && !searching && !notFound && !error && (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <Computer sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Busque un equipo por su número de serie
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Ingrese el número de serie completo del MFP para ver su información, contratos y lecturas.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Equipment;
